import { match as pathMatch } from 'path-to-regexp';
import { appRegistry } from './appRegistry';
import { routeRegistry } from './routeRegistry';
import type { ModuleRoute, UnresolvedAppConfig, RouteRegistryEntry } from '../types';
import { calculateRouteSpecificity } from './utils';

/**
 * Unauthenticated Route Matcher
 *
 * Scans both the app registry and route registry for entries with
 * `unauthenticatedRoutes` and builds cached matchers at module load time.
 * Used by AuthGate to decide whether to bypass the auth gate for a given pathname.
 */

type RegistryEntry = UnresolvedAppConfig | RouteRegistryEntry;

interface CachedMatcher {
  entry: RegistryEntry;
  route: ModuleRoute;
  matcher: (path: string) => ReturnType<ReturnType<typeof pathMatch>>;
  specificity: number;
}

interface UnauthenticatedRouteMatch {
  entry: RegistryEntry;
  matchedRoute: ModuleRoute;
}

/**
 * Build matchers once at module load time.
 * Iterates all entries from both registries and compiles path-to-regexp matchers
 * for entries that declare `unauthenticatedRoutes`.
 */
function buildCachedMatchers(): CachedMatcher[] {
  const matchers: CachedMatcher[] = [];

  const allEntries: RegistryEntry[] = [...appRegistry, ...routeRegistry];

  for (const entry of allEntries) {
    // unauthenticatedRoutes only exists on RemoteAppConfig and RouteRegistryEntry
    const unauthRoutes = 'unauthenticatedRoutes' in entry ? entry.unauthenticatedRoutes : undefined;
    if (!unauthRoutes || unauthRoutes.length === 0) continue;

    for (const route of unauthRoutes) {
      const fullPattern = entry.basePath + (route.pattern === '/' ? '' : route.pattern);
      // end: false allows prefix matching (e.g., /access/registration/complete/step-2 still matches)
      const matcher = pathMatch(fullPattern, { end: false });
      const specificity = calculateRouteSpecificity(fullPattern);

      matchers.push({ entry, route, matcher, specificity });
    }

    // Dev-time collision warning
    if (import.meta.env.DEV) {
      for (const route of unauthRoutes) {
        const fullPattern = entry.basePath + (route.pattern === '/' ? '' : route.pattern);
        // Check if this basePath also exists in an authenticated context
        const hasAuthCollision = allEntries.some(
          (other) =>
            other !== entry &&
            other.basePath === entry.basePath &&
            'moduleRoutes' in other &&
            other.moduleRoutes &&
            other.moduleRoutes.length > 0
        );
        if (hasAuthCollision) {
          console.warn(
            `[unauthenticatedRouteMatcher] Unauthenticated pattern "${fullPattern}" shares basePath ` +
              `"${entry.basePath}" with an authenticated app. Unauthenticated routes take precedence.`
          );
        }
      }
    }
  }

  return matchers;
}

const cachedMatchers = buildCachedMatchers();

/**
 * Checks if a pathname matches any unauthenticated route across both registries.
 */
export function isUnauthenticatedRoute(pathname: string): boolean {
  return cachedMatchers.some((m) => m.matcher(pathname) !== false);
}

/**
 * Returns the matched registry entry and route for a given pathname,
 * or null if no unauthenticated route matches.
 * When multiple patterns match, the most specific one is selected.
 */
export function getUnauthenticatedRouteMatch(pathname: string): UnauthenticatedRouteMatch | null {
  const matches = cachedMatchers
    .filter((m) => m.matcher(pathname) !== false)
    .sort((a, b) => b.specificity - a.specificity);

  if (matches.length === 0) return null;

  const best = matches[0];
  return { entry: best.entry, matchedRoute: best.route };
}
